import { supabase } from '../supabase';
import type { Clinic, ClinicInsert, ClinicUpdate } from '../types';

/**
 * Get all active clinics
 */
export const getClinics = async (filters?: {
  city?: string;
  state?: string;
  search?: string;
  activeOnly?: boolean;
}): Promise<Clinic[]> => {
  let query = supabase.from('clinics').select('*');

  // Default to active only if not specified
  if (filters?.activeOnly !== false) {
    query = query.eq('is_active', true);
  }

  if (filters?.city) {
    query = query.eq('city', filters.city);
  }

  if (filters?.state) {
    query = query.eq('state', filters.state);
  }

  if (filters?.search) {
    query = query.or(`name.ilike.%${filters.search}%,city.ilike.%${filters.search}%,address.ilike.%${filters.search}%`);
  }

  const { data, error } = await query.order('name');

  if (error) throw error;
  return data || [];
};

/**
 * Get clinic by ID
 */
export const getClinicById = async (id: string): Promise<Clinic | null> => {
  const { data, error } = await supabase
    .from('clinics')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data;
};

/**
 * Create clinic (admin only)
 */
export const createClinic = async (clinic: ClinicInsert): Promise<Clinic> => {
  const { data, error } = await supabase
    .from('clinics')
    .insert(clinic)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Update clinic (admin only)
 */
export const updateClinic = async (id: string, updates: ClinicUpdate): Promise<Clinic> => {
  const { data, error } = await supabase
    .from('clinics')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Delete clinic (admin only)
 */
export const deleteClinic = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('clinics')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

/**
 * Get clinics by city
 */
export const getClinicsByCity = async (city: string): Promise<Clinic[]> => {
  const { data, error } = await supabase
    .from('clinics')
    .select('*')
    .eq('is_active', true)
    .eq('city', city)
    .order('name');

  if (error) throw error;
  return data || [];
};

/**
 * Get clinics by state
 */
export const getClinicsByState = async (state: string): Promise<Clinic[]> => {
  const { data, error } = await supabase
    .from('clinics')
    .select('*')
    .eq('is_active', true)
    .eq('state', state)
    .order('name');

  if (error) throw error;
  return data || [];
};

/**
 * Search clinics
 */
export const searchClinics = async (query: string): Promise<Clinic[]> => {
  const { data, error } = await supabase
    .from('clinics')
    .select('*')
    .eq('is_active', true)
    .or(`name.ilike.%${query}%,city.ilike.%${query}%,address.ilike.%${query}%`)
    .order('name')
    .limit(20);

  if (error) throw error;
  return data || [];
};
